import { useState, useRef, useEffect } from 'react'
import { CalendarEvent } from '@/types/calendar'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, Users, Sparkles } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { motion, AnimatePresence } from 'framer-motion'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, isToday, startOfDay } from 'date-fns'
import { TimeBlockSidebar, timeBlocks, type TimeBlock } from './TimeBlockSidebar'
import { calendarAI } from '@/services/calendarAI'
import { toast } from 'sonner'

interface AdvancedCalendarProps {
  events: CalendarEvent[]
  onEventCreate?: (event: any) => void
  onEventUpdate?: (event: any) => void
  onEventDelete?: (eventId: string) => void
  onEventClick?: (event: CalendarEvent) => void
  startDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  weekOptions?: {
    showNowIndicator?: boolean
    narrowWeekend?: boolean
    hourStart?: number
    hourEnd?: number
  }
  monthOptions?: {
    visibleWeeksCount?: number
  }
}

type ViewMode = 'day' | 'week' | 'month'

export function AdvancedCalendar({ 
  events = [], 
  onEventClick,
  onEventCreate,
  onEventUpdate,
  startDayOfWeek = 0,
  weekOptions = {},
  monthOptions = {}
}: AdvancedCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState<ViewMode>('week')
  const [draggedBlock, setDraggedBlock] = useState<TimeBlock | null>(null)
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null)
  const [dropTarget, setDropTarget] = useState<{ date: Date; hour: number; minute: number } | null>(null)
  const [categorizingEvent, setCategorizingEvent] = useState<string | null>(null)
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null)
  const [zoomLevel, setZoomLevel] = useState<number>(60) // Default: 60px per hour
  const [isDragging, setIsDragging] = useState(false)
  const [aiColoredEvents, setAiColoredEvents] = useState<Map<string, string>>(new Map())
  const calendarRef = useRef<HTMLDivElement>(null)

  // Navigation handlers
  const goToPrevious = () => {
    switch (view) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1))
        break
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1))
        break
      case 'month':
        setCurrentDate(subMonths(currentDate, 1))
        break
    }
  }

  const goToNext = () => {
    switch (view) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1))
        break
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1))
        break
      case 'month':
        setCurrentDate(addMonths(currentDate, 1))
        break
    }
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  // Get view title
  const getViewTitle = () => {
    switch (view) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy')
      case 'week':
        const weekStart = startOfWeek(currentDate, { weekStartsOn: startDayOfWeek })
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: startDayOfWeek })
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
      case 'month':
        return format(currentDate, 'MMMM yyyy')
      default:
        return format(currentDate, 'MMMM yyyy')
    }
  }

  // Auto-color events with AI - DISABLED to prevent rate limit loops
  // Colors are now based on category field only
  useEffect(() => {
    // Skip AI categorization entirely - use category field from event
    // This prevents rate limit loops and duplicate categorization
    const newColors = new Map<string, string>()
    
    for (const event of events) {
      if (!aiColoredEvents.has(event.id)) {
        // Use existing category if available, otherwise use fallback
        const category = event.category || 'Uncategorized'
        newColors.set(event.id, getColorForCategory(category))
      }
    }
    
    if (newColors.size > 0) {
      setAiColoredEvents(prev => new Map([...prev, ...newColors]))
    }
  }, [events])

  // Get AI-generated color for event - use color from database if available
  const getEventColor = (event: CalendarEvent) => {
    // First priority: explicit color field from database
    if (event.color) {
      return `from-[${event.color}] to-[${event.color}]`
    }
    // Second priority: category-based color
    return aiColoredEvents.get(event.id) || getCategoryColor(event.category)
  }

  // Calculate zoom level based on event durations
  const calculateAutoZoom = (events: CalendarEvent[]) => {
    if (events.length === 0) return 60
    
    const durations = events.map(e => 
      (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / (1000 * 60)
    )
    
    const minDuration = Math.min(...durations)
    const maxDuration = Math.max(...durations)
    
    // Smallest event gets most zoom (largest height)
    // Largest event gets least zoom (smallest height)
    // Base calculation: inverse relationship
    if (minDuration < 30) {
      return 120 // 2x zoom for events < 30 min
    } else if (maxDuration > 120) {
      return 40 // 0.67x zoom for events > 2 hours
    }
    
    return 60 // Default zoom
  }

  // Drag and drop handlers with 2-second hover auto-zoom
  const handleBlockDragStart = (block: TimeBlock) => {
    setDraggedBlock(block)
    setIsDragging(true)
  }

  const handleEventDragStart = (e: React.DragEvent, event: CalendarEvent) => {
    e.stopPropagation()
    setDraggedEvent(event)
    setIsDragging(true)
  }

  const handleBlockDragEnd = () => {
    setDraggedBlock(null)
    setDraggedEvent(null)
    setDropTarget(null)
    setIsDragging(false)
    
    // Clear hover timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      setHoverTimeout(null)
    }
  }

  const handleDragOver = (e: React.DragEvent, date: Date, hour?: number, minute?: number) => {
    e.preventDefault()
    
    if (draggedBlock || draggedEvent) {
      const targetMinute = minute ?? 0
      setDropTarget({ date, hour: hour ?? 9, minute: targetMinute })
      
      // Start 2-second hover timer for auto-zoom
      if (!hoverTimeout) {
        const timeout = setTimeout(() => {
          // Calculate and apply auto-zoom based on visible events
          const visibleEvents = events.filter(ev => {
            const evDate = new Date(ev.startTime)
            return evDate.toDateString() === date.toDateString()
          })
          
          const newZoom = calculateAutoZoom(visibleEvents)
          setZoomLevel(newZoom)
        }, 2000)
        
        setHoverTimeout(timeout)
      }
    }
  }

  const handleDragLeave = () => {
    // Clear hover timeout when leaving
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      setHoverTimeout(null)
    }
  }

  const handleDrop = async (e: React.DragEvent, date: Date, hour?: number, minute?: number) => {
    e.preventDefault()
    
    const targetMinute = minute ?? 0
    
    try {
      // Handle dropping time blocks
      if (draggedBlock) {
        const startTime = new Date(date)
        startTime.setHours(hour ?? 9, targetMinute, 0, 0)
        
        const endTime = new Date(startTime)
        endTime.setMinutes(endTime.getMinutes() + draggedBlock.defaultDuration)

        const newEvent = {
          title: draggedBlock.title,
          startTime,
          endTime,
          eventType: 'task' as const,
          category: draggedBlock.category,
          location: ''
        }

        if (onEventCreate) {
          await onEventCreate(newEvent)
          
          toast.success(`${draggedBlock.title} scheduled!`, {
            description: `${format(startTime, 'h:mm a')} - ${format(endTime, 'h:mm a')}`
          })
        }
      }
      
      // Handle moving existing events
      if (draggedEvent && onEventUpdate) {
        const duration = new Date(draggedEvent.endTime).getTime() - new Date(draggedEvent.startTime).getTime()
        
        const newStartTime = new Date(date)
        newStartTime.setHours(hour ?? 9, targetMinute, 0, 0)
        
        const newEndTime = new Date(newStartTime.getTime() + duration)
        
        await onEventUpdate({
          id: draggedEvent.id,
          startTime: newStartTime,
          endTime: newEndTime
        })
        
        toast.success(`${draggedEvent.title} rescheduled!`, {
          description: `${format(newStartTime, 'h:mm a')} - ${format(newEndTime, 'h:mm a')}`
        })
      }
    } catch (error: any) {
      console.error('❌ Error creating event:', error)
      toast.error('Failed to create event', {
        description: error.message || 'Please try again'
      })
    } finally {
      // Always clean up drag state
      setDraggedBlock(null)
      setDraggedEvent(null)
      setDropTarget(null)
      setIsDragging(false)
      
      if (hoverTimeout) {
        clearTimeout(hoverTimeout)
        setHoverTimeout(null)
      }
    }
  }

  // Event click handler - triggers parent click handler for edit modal
  const handleEventClick = (event: CalendarEvent) => {
    console.log('🖱️ Event clicked in AdvancedCalendar:', event.id, event.title)
    
    // Trigger parent click handler for full edit modal
    if (onEventClick) {
      onEventClick(event)
    }
  }

  // Auto-categorize event with AI badge
  const autoCategorizeEvent = async (eventId: string, title: string) => {
    setCategorizingEvent(eventId)
    
    // Simple keyword-based categorization for instant feedback
    const keywords: Record<string, string> = {
      'meeting': 'Work',
      'code': 'Work',
      'write': 'Work',
      'design': 'Work',
      'lunch': 'Personal',
      'break': 'Personal',
      'exercise': 'Health',
      'workout': 'Health',
      'gym': 'Health',
      'call': 'Work',
      'review': 'Work'
    }

    let category = 'Personal'
    const lowerTitle = title.toLowerCase()
    
    for (const [keyword, cat] of Object.entries(keywords)) {
      if (lowerTitle.includes(keyword)) {
        category = cat
        break
      }
    }

    // Show AI processing animation briefly
    await new Promise(resolve => setTimeout(resolve, 800))

    if (onEventUpdate) {
      await onEventUpdate({ id: eventId, category })
    }

    setCategorizingEvent(null)
  }

  // Get color for category
  const getCategoryColor = (category?: string) => {
    const colors: Record<string, string> = {
      'Work': 'from-blue-500 to-cyan-600',
      'Personal': 'from-purple-500 to-pink-600',
      'Health': 'from-green-500 to-emerald-600',
      'Education': 'from-orange-500 to-red-600'
    }
    return colors[category || ''] || 'from-gray-500 to-slate-600'
  }

  // Get color based on AI category name
  const getColorForCategory = (category: string) => {
    const colorMap: Record<string, string> = {
      'Work': 'from-blue-500 to-cyan-600',
      'Personal': 'from-purple-500 to-pink-600',
      'Health': 'from-green-500 to-emerald-600',
      'Education': 'from-orange-500 to-red-600',
      'Social': 'from-pink-500 to-rose-600',
      'Finance': 'from-yellow-500 to-amber-600',
      'Travel': 'from-indigo-500 to-purple-600',
      'Shopping': 'from-teal-500 to-cyan-600',
      'Entertainment': 'from-red-500 to-pink-600',
      'Family': 'from-orange-500 to-yellow-600',
      'Uncategorized': 'from-gray-500 to-slate-600'
    }
    return colorMap[category] || 'from-gray-500 to-slate-600'
  }

  // Render Week View with drag-and-drop and 5-minute chunks
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: startDayOfWeek })
    const weekDays = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) })
    const hours = Array.from({ length: 24 }, (_, i) => i)
    const currentTime = new Date()
    const showNowLine = weekOptions.showNowIndicator !== false

    // Generate 5-minute time slots for each hour
    const generateTimeSlots = (hour: number) => {
      return Array.from({ length: 12 }, (_, i) => ({
        hour,
        minute: i * 5,
        label: `${hour.toString().padStart(2, '0')}:${(i * 5).toString().padStart(2, '0')}`
      }))
    }

    // Calculate pixel height per 5-minute slot based on zoom
    const pixelsPerMinute = zoomLevel / 60
    const slotHeight = 5 * pixelsPerMinute

    return (
      <div className="flex gap-4">
        {/* Time Block Sidebar */}
        <div className="w-64 flex-shrink-0">
          <TimeBlockSidebar 
            onBlockDragStart={handleBlockDragStart}
            onBlockDragEnd={handleBlockDragEnd}
          />
        </div>

        {/* Calendar Grid */}
        <div className="flex-1 glass-card rounded-xl overflow-hidden" ref={calendarRef}>
          {/* Week Header */}
          <div className="grid grid-cols-8 border-b border-border/50">
            <div className="p-3 text-xs font-semibold text-muted-foreground">Time</div>
            {weekDays.map(day => (
              <div 
                key={day.toISOString()} 
                className={`p-3 text-center border-l border-border/50 ${
                  isToday(day) ? 'bg-primary/10' : ''
                }`}
              >
                <div className="text-xs font-semibold text-muted-foreground">
                  {format(day, 'EEE')}
                </div>
                <div className={`text-2xl font-bold mt-1 ${
                  isToday(day) ? 'text-primary' : ''
                }`}>
                  {format(day, 'd')}
                </div>
              </div>
            ))}
          </div>

          {/* Time Grid with 5-minute chunks */}
          <div className="relative overflow-auto max-h-[600px]">
            {hours.map(hour => {
              const slots = generateTimeSlots(hour)

              return (
                <div key={hour} className="relative">
                  {/* Hour row container */}
                  <div className="grid grid-cols-8 border-b border-border/30 relative">
                    {/* Hour Label */}
                    <div className="p-2 text-xs text-muted-foreground flex items-start">
                      {format(new Date().setHours(hour, 0), 'h a')}
                    </div>

                    {/* Day Columns with 5-minute slots */}
                    {weekDays.map(day => {
                      // Get events for this hour in this day
                      const cellEvents = events.filter(e => {
                        const eventStart = new Date(e.startTime)
                        const eventHour = eventStart.getHours()
                        return eventStart.toDateString() === day.toDateString() && 
                               eventHour === hour
                      })

                      return (
                        <div key={`${day.toISOString()}-${hour}`} className="border-l border-border/30 relative">
                          {/* 5-minute slot grid */}
                          {slots.map((slot) => {
                            const isSlotDrop = dropTarget?.date?.getDate() === day.getDate() &&
                                               dropTarget?.hour === hour &&
                                               dropTarget?.minute === slot.minute

                            return (
                              <div
                                key={`${day.toISOString()}-${slot.label}`}
                                className={`border-t border-border/10 transition-all duration-200 ${isSlotDrop ? 'bg-primary/20 ring-2 ring-primary' : 'hover:bg-muted/30'}`}
                                style={{ height: `${slotHeight}px` }}
                                onDragOver={(e) => handleDragOver(e, day, hour, slot.minute)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, day, hour, slot.minute)}
                              >
                                {/* Drop indicator */}
                                {isSlotDrop && (draggedBlock || draggedEvent) && (
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                                    <div className="text-xs font-semibold text-primary bg-white/90 px-2 py-1 rounded">
                                      Drop at {slot.label}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}

                          {/* Events positioned on top of slots */}
                          <div className="absolute inset-0 pointer-events-none">
                            <AnimatePresence>
                              {cellEvents.map(event => {
                                const eventStart = new Date(event.startTime)
                                const eventEnd = new Date(event.endTime)
                                const startMinute = eventStart.getMinutes()
                                const duration = (eventEnd.getTime() - eventStart.getTime()) / (1000 * 60)
                                const topOffset = startMinute * pixelsPerMinute
                                const eventHeight = duration * pixelsPerMinute

                                return (
                                  <motion.div
                                    key={event.id}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    draggable
                                    onDragStart={(e) => handleEventDragStart(e, event)}
                                    onDragEnd={handleBlockDragEnd}
                                    onClick={() => handleEventClick(event)}
                                    className={`absolute left-1 right-1 rounded-lg p-2 cursor-move bg-gradient-to-r ${getEventColor(event)} text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 z-20 pointer-events-auto`}
                                    style={{ 
                                      top: `${topOffset}px`,
                                      height: `${Math.max(eventHeight, slotHeight)}px`
                                    }}
                                  >
                                    <div className="flex items-start justify-between gap-1 h-full">
                                      <div className="flex-1 min-w-0 overflow-hidden">
                                        <div className="text-xs font-semibold truncate">{event.title}</div>
                                        <div className="text-[10px] opacity-80 truncate">
                                          {format(eventStart, 'h:mm a')}
                                        </div>
                                        {event.location && (
                                          <div className="text-[10px] opacity-80 truncate flex items-center gap-1 mt-0.5">
                                            <MapPin className="w-2.5 h-2.5" />
                                            {event.location}
                                          </div>
                                        )}
                                      </div>
                                      {categorizingEvent === event.id && (
                                        <motion.div
                                          animate={{ rotate: 360 }}
                                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                        >
                                          <Sparkles className="w-3 h-3" />
                                        </motion.div>
                                      )}
                                    </div>
                                  </motion.div>
                                )
                              })}
                            </AnimatePresence>
                          </div>
                        </div>
                      )
                    })}

                    {/* Current Time Indicator */}
                    {showNowLine && hour === currentTime.getHours() && (
                      <div 
                        className="absolute left-0 right-0 h-0.5 bg-red-500 z-40 pointer-events-none"
                        style={{
                          top: `${currentTime.getMinutes() * pixelsPerMinute}px`
                        }}
                      >
                        <div className="absolute -left-2 -top-2 w-4 h-4 rounded-full bg-red-500 animate-pulse" />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // Render Month View
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const startDate = startOfWeek(monthStart, { weekStartsOn: startDayOfWeek })
    const endDate = endOfWeek(monthEnd, { weekStartsOn: startDayOfWeek })
    
    const days = eachDayOfInterval({ start: startDate, end: endDate })
    const weekDays = eachDayOfInterval({ start: startDate, end: addDays(startDate, 6) })

    return (
      <div className="glass-card rounded-xl overflow-hidden">
        {/* Week Day Headers */}
        <div className="grid grid-cols-7 border-b border-border/50">
          {weekDays.map(day => (
            <div key={day.toISOString()} className="p-3 text-center text-xs font-semibold text-muted-foreground">
              {format(day, 'EEE')}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {days.map(day => {
            const dayEvents = events.filter(e => {
              const eventStart = new Date(e.startTime)
              return isSameDay(eventStart, day)
            })

            return (
              <div
                key={day.toISOString()}
                className={`min-h-[120px] p-2 border-r border-b border-border/30 ${
                  !isSameMonth(day, currentDate) ? 'bg-muted/30' : ''
                } ${isToday(day) ? 'bg-primary/5' : ''} hover:bg-muted/50 transition-colors duration-200`}
                onDragOver={(e) => handleDragOver(e, day)}
                onDrop={(e) => handleDrop(e, day)}
              >
                <div className={`text-sm font-semibold mb-2 ${
                  isToday(day) ? 'text-primary' : 
                  !isSameMonth(day, currentDate) ? 'text-muted-foreground' : ''
                }`}>
                  {format(day, 'd')}
                </div>

                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(event => (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={() => handleEventClick(event)}
                      className={`text-xs p-1.5 rounded bg-gradient-to-r ${getCategoryColor(event.category)} text-white cursor-pointer hover:shadow-md transition-all duration-200 truncate`}
                    >
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{event.title}</span>
                      </div>
                    </motion.div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10px] text-muted-foreground pl-1">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToPrevious} className="h-9">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday} className="h-9 px-4">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goToNext} className="h-9">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="text-lg font-bold ml-2">{getViewTitle()}</div>
        </div>

        <Select value={view} onValueChange={(v) => setView(v as ViewMode)}>
          <SelectTrigger className="w-[120px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Day</SelectItem>
            <SelectItem value="week">Week</SelectItem>
            <SelectItem value="month">Month</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Calendar Views */}
      <AnimatePresence mode="wait">
        <motion.div
          key={view}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {view === 'week' && renderWeekView()}
          {view === 'month' && renderMonthView()}
        </motion.div>
      </AnimatePresence>

    </div>
  )
}
